import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Award,
  Gift,
  Loader2,
  LogOut,
  Mail,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { useAuth } from "@/context/AuthContext";

export default function AdminClients() {
  const { user, loading, logout, authHeader, API } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | freebies | active

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setClients(data);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 403) setError("This account does not have admin access.");
      else if (e?.response?.status === 401) setError("Please sign in with the admin account.");
      else setError("Could not load clients.");
    }
  }, [API, authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    if (!user.is_admin) {
      setError("This account does not have admin access.");
      return;
    }
    fetchClients();
  }, [user, loading, navigate, fetchClients]);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (filter === "freebies" && c.free_credits <= 0) return false;
      if (filter === "active" && c.lead_count === 0) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    });
  }, [clients, search, filter]);

  if (loading || (!clients && !error)) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="admin-clients-loading">
        <Nav />
        <div className="pt-40 flex items-center justify-center text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading clients…
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="admin-clients-no-access">
        <Nav />
        <div className="pt-40 px-6 lg:px-12 max-w-2xl mx-auto">
          <div className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center">
            <div className="inline-flex h-12 w-12 rounded-full bg-rose-100 items-center justify-center mb-4">
              <ShieldCheck className="h-5 w-5 text-rose-700" />
            </div>
            <h1 className="font-display text-3xl font-semibold">Access restricted</h1>
            <p className="text-stone-600 mt-3">{error}</p>
            <Link
              to="/admin"
              className="mt-6 inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to /admin
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAF7F2] text-stone-900" data-testid="admin-clients-root">
      <Nav />

      <section className="pt-32 pb-10 lg:pt-40 lg:pb-12 px-6 lg:px-12 bg-bubbly relative overflow-hidden">
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[320px] h-[320px] top-1/3 -right-24" />
        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/admin"
            data-testid="admin-clients-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Admin home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                <Users className="h-3.5 w-3.5 text-teal-700" /> Clients
              </span>
              <h1 className="font-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.02]">
                Your client roster
              </h1>
              <p className="mt-4 text-stone-600 text-lg leading-relaxed max-w-xl">
                Points, orders, and free credits at a glance. Click any client to
                see their full timeline and upload their finished floor plans.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="text-xs text-stone-500 mb-1">Signed in as</div>
                <div className="flex items-center gap-2 text-stone-900 font-semibold mb-3 break-all">
                  <Mail className="h-4 w-4 text-teal-700 flex-shrink-0" /> {user?.email}
                </div>
                <button
                  onClick={async () => {
                    await logout();
                    toast.message("Signed out");
                    navigate("/");
                  }}
                  data-testid="admin-clients-signout-btn"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 px-6 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                data-testid="admin-clients-search"
                className="w-full bg-white border border-stone-200 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-teal-700"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="admin-clients-filter">
              {[
                { id: "all", label: "All" },
                { id: "active", label: "Active (≥1 order)" },
                { id: "freebies", label: "Has free credits" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`clients-filter-${f.id}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                    filter === f.id
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center" data-testid="admin-clients-empty">
              <Users className="h-6 w-6 text-stone-400 mx-auto mb-3" />
              <h3 className="font-display text-xl font-semibold">No clients match</h3>
              <p className="text-stone-600 mt-2">Try clearing your filters.</p>
            </div>
          ) : (
            <div
              className="bg-white border border-stone-200 rounded-[2rem] overflow-hidden"
              data-testid="admin-clients-table"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-4 font-semibold">Client</th>
                      <th className="text-left px-5 py-4 font-semibold">Points</th>
                      <th className="text-left px-5 py-4 font-semibold">Credits</th>
                      <th className="text-left px-5 py-4 font-semibold">Orders</th>
                      <th className="text-left px-5 py-4 font-semibold">Joined</th>
                      <th className="text-right px-5 py-4 font-semibold">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => {
                      const joined = c.created_at ? new Date(c.created_at) : null;
                      return (
                        <tr
                          key={c.id}
                          className="border-t border-stone-100 hover:bg-stone-50/60 transition-colors"
                          data-testid={`admin-client-row-${c.id}`}
                        >
                          <td className="px-5 py-4 align-top">
                            <div className="font-semibold text-stone-900">
                              {c.name || c.email}
                            </div>
                            <div className="text-xs text-stone-500 break-all">{c.email}</div>
                          </td>
                          <td className="px-5 py-4 align-top">
                            <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                              <Award className="h-3 w-3" /> {c.points} pts
                            </div>
                            <div className="text-[11px] text-stone-500 mt-1">
                              Lifetime {c.lifetime_points}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top">
                            {c.free_credits > 0 ? (
                              <div className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-800 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                                <Gift className="h-3 w-3" /> {c.free_credits} pending
                              </div>
                            ) : (
                              <span className="text-stone-400 text-xs">—</span>
                            )}
                            {c.free_credits_used > 0 && (
                              <div className="text-[11px] text-stone-500 mt-1">
                                {c.free_credits_used} used
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top">
                            <div className="text-stone-900 font-semibold">{c.lead_count}</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                              {c.leads_paid}P · {c.leads_delivered}D · {c.leads_in_progress}I · {c.leads_new}N
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top text-stone-600 text-xs">
                            {joined
                              ? joined.toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "—"}
                          </td>
                          <td className="px-5 py-4 align-top text-right">
                            <Link
                              to={`/admin/clients/${c.id}`}
                              data-testid={`admin-open-client-${c.id}`}
                              className="inline-flex items-center gap-1.5 bg-stone-900 text-white px-3.5 py-1.5 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
